import { GATEWAY_ZONE_GEOMETRY } from "./generated/gateway-zone-geometry.js";
export {
  GATEWAY_ZONE_GEOMETRY_SOURCE_SHA256,
  GATEWAY_ZONE_GEOMETRY_SOURCE_URL
} from "./generated/gateway-zone-geometry.js";

export type GatewayZoneKind = "sanctuary" | "migration" | "patrol";
export type GatewayZoneGeometry = (typeof GATEWAY_ZONE_GEOMETRY)[number];
export type GatewayZoneShape = GatewayZoneGeometry["shape"];

export function gatewayZones(kind: GatewayZoneKind): readonly GatewayZoneGeometry[] {
  return GATEWAY_ZONE_GEOMETRY.filter((zone) => zone.kind === kind);
}

export function gatewayZoneCount(kind: GatewayZoneKind): number {
  return gatewayZones(kind).length;
}

export function gatewayZonesAtWorldPosition(
  kind: GatewayZoneKind,
  worldY: number,
  worldX: number
): string[] {
  if (!Number.isFinite(worldX) || !Number.isFinite(worldY)) return [];
  return gatewayZones(kind)
    .filter((zone) => gatewayZoneContainsWorldPosition(zone, worldX, worldY))
    .map((zone) => zone.id);
}

export function gatewayZoneContainsWorldPosition(
  zone: GatewayZoneGeometry,
  worldX: number,
  worldY: number
): boolean {
  if (!Number.isFinite(worldX) || !Number.isFinite(worldY)) return false;
  const shape = zone.shape;
  if (shape.type === "circle") {
    const dx = worldX - shape.centerX;
    const dy = worldY - shape.centerY;
    return (dx * dx) + (dy * dy) <= shape.radius * shape.radius;
  }
  if (shape.type === "box") {
    const radians = shape.yaw * Math.PI / 180;
    const cosine = Math.cos(radians);
    const sine = Math.sin(radians);
    const dx = worldX - shape.centerX;
    const dy = worldY - shape.centerY;
    const localX = (dx * cosine) + (dy * sine);
    const localY = (-dx * sine) + (dy * cosine);
    return Math.abs(localX) <= shape.halfX && Math.abs(localY) <= shape.halfY;
  }
  return pointInPolygon(worldX, worldY, shape.points);
}

export function gatewayZoneBoundaryWorldPoints(
  zone: GatewayZoneGeometry,
  circleSegments = 48
): Array<{ worldX: number; worldY: number }> {
  const shape = zone.shape;
  if (shape.type === "polygon") {
    return shape.points.map((point) => ({ worldX: point.x, worldY: point.y }));
  }
  if (shape.type === "circle") {
    const count = Math.max(16, Math.min(96, Math.round(circleSegments)));
    return Array.from({ length: count }, (_, index) => {
      const angle = (index / count) * Math.PI * 2;
      return {
        worldX: shape.centerX + (Math.cos(angle) * shape.radius),
        worldY: shape.centerY + (Math.sin(angle) * shape.radius)
      };
    });
  }
  const radians = shape.yaw * Math.PI / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  return [
    [-shape.halfX, -shape.halfY],
    [shape.halfX, -shape.halfY],
    [shape.halfX, shape.halfY],
    [-shape.halfX, shape.halfY]
  ].map(([localX, localY]) => ({
    worldX: shape.centerX + (localX * cosine) - (localY * sine),
    worldY: shape.centerY + (localX * sine) + (localY * cosine)
  }));
}

function pointInPolygon(
  worldX: number,
  worldY: number,
  points: readonly { x: number; y: number }[]
): boolean {
  let inside = false;
  for (let current = 0, previous = points.length - 1; current < points.length; previous = current, current += 1) {
    const a = points[previous]!;
    const b = points[current]!;
    if (pointOnSegment(worldX, worldY, a.x, a.y, b.x, b.y)) return true;
    const crosses = (a.y > worldY) !== (b.y > worldY)
      && worldX < ((b.x - a.x) * (worldY - a.y) / (b.y - a.y)) + a.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

function pointOnSegment(
  x: number,
  y: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): boolean {
  const cross = ((x - ax) * (by - ay)) - ((y - ay) * (bx - ax));
  if (Math.abs(cross) > 0.01) return false;
  const dot = ((x - ax) * (bx - ax)) + ((y - ay) * (by - ay));
  if (dot < 0) return false;
  const lengthSquared = ((bx - ax) ** 2) + ((by - ay) ** 2);
  return dot <= lengthSquared;
}
